const PALETTE = [
  { bg: 'bg-indigo-100', text: 'text-indigo-700', dot: '#6366f1', border: 'border-indigo-200', dotBg: 'bg-indigo-500' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: '#10b981', border: 'border-emerald-200', dotBg: 'bg-emerald-500' },
  { bg: 'bg-rose-100', text: 'text-rose-700', dot: '#f43f5e', border: 'border-rose-200', dotBg: 'bg-rose-500' },
  { bg: 'bg-amber-100', text: 'text-amber-700', dot: '#f59e0b', border: 'border-amber-200', dotBg: 'bg-amber-500' },
  { bg: 'bg-sky-100', text: 'text-sky-700', dot: '#0ea5e9', border: 'border-sky-200', dotBg: 'bg-sky-500' },
  { bg: 'bg-violet-100', text: 'text-violet-700', dot: '#8b5cf6', border: 'border-violet-200', dotBg: 'bg-violet-500' },
  { bg: 'bg-teal-100', text: 'text-teal-700', dot: '#14b8a6', border: 'border-teal-200', dotBg: 'bg-teal-500' },
  { bg: 'bg-orange-100', text: 'text-orange-700', dot: '#f97316', border: 'border-orange-200', dotBg: 'bg-orange-500' },
] as const

export type CompanyColor = {
  bg: (typeof PALETTE)[number]['bg']
  text: (typeof PALETTE)[number]['text']
  dot: string
  border: (typeof PALETTE)[number]['border']
  dotBg: (typeof PALETTE)[number]['dotBg']
}

/**
 * Convert a 3- or 6-digit hex colour to an `rgba()` string at the given alpha.
 * Used for inline-styled tints (card backgrounds, accents) where Tailwind's
 * static classes can't express an arbitrary company-chosen hex. Falls back to a
 * transparent value for malformed input so callers never render a broken style.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '').trim()
  const full =
    normalized.length === 3
      ? normalized.split('').map((c) => c + c).join('')
      : normalized
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return `rgba(0, 0, 0, 0)`
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function hash(id: string): number {
  let h = 0
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

/**
 * Returns palette classes for a company. If `override` (Company.color from the DB)
 * is a valid hex, it overrides the `dot` hex used by inline-styled accents while
 * the class-based chip/border colours stay on the palette (Tailwind needs static classes).
 */
export function getCompanyColor(
  companyId: string,
  override?: string,
): CompanyColor {
  const base = !companyId ? PALETTE[0] : PALETTE[hash(companyId) % PALETTE.length]
  if (override && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(override)) {
    return { ...base, dot: override }
  }
  return base
}
